import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { profileService } from '../api/services/profile.service'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend } from '../components/ui/field-label'
import { PasswordInput } from '../components/ui/password-input'
import { PASSWORD_HINT, changePasswordFormSchema } from '../lib/password'

const credentialsSchema = changePasswordFormSchema

type CredentialsFormValues = z.infer<typeof credentialsSchema>

export function UpdateCredentialsPage() {
  const form = useForm<CredentialsFormValues>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: {
      current_password: '',
      password: '',
      password_confirmation: '',
    },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await profileService.changePassword(values)
      form.reset()
    } catch {
      // API errors are toasted globally by the axios interceptor.
    }
  })

  return (
    <AppShell>
      <section className="mx-auto max-w-xl rounded-lg border border-parch-100 bg-white p-6 shadow-card">
        <h1 className="font-display text-2xl font-bold">Update Credentials</h1>
        <p className="mt-1 text-sm text-ink-600">
          Change your account password. You will need your current password to continue.
        </p>

        <form className="mt-6 space-y-4" onSubmit={onSubmit}>
          <FormRequiredLegend />
          <div className="space-y-2">
            <FieldLabel required htmlFor="current_password">
              Current password
            </FieldLabel>
            <PasswordInput id="current_password" {...form.register('current_password')} />
          </div>

          <div className="space-y-2">
            <FieldLabel required htmlFor="password">
              New password
            </FieldLabel>
            <p className="text-xs text-ink-500">{PASSWORD_HINT}</p>
            <PasswordInput id="password" {...form.register('password')} />
            {form.formState.errors.password && (
              <p className="text-xs text-danger">{form.formState.errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <FieldLabel required htmlFor="password_confirmation">
              Confirm new password
            </FieldLabel>
            <PasswordInput id="password_confirmation" {...form.register('password_confirmation')} />
            {form.formState.errors.password_confirmation && (
              <p className="text-xs text-danger">{form.formState.errors.password_confirmation.message}</p>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </form>
      </section>
    </AppShell>
  )
}
